import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create } from "@mui/icons-material";

import { EntityInput } from "@dzambalaorg/mui-inputs-entity";
import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { getEmptyTemplate } from "@dzambalaorg/mui-inputs-asset";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { ContractFeatures, IRent, RentRuleStatus, TokenType } from "@framework/types";
import { useDialogForm, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { Button, List } from "@dzambalaorg/ui-kit";

import { RentDialogForm } from "./form";

const RENT_DIALOG_KEY = "rents";
const RENT_URL = "/rents";
const emptyValues = {
  price: getEmptyTemplate(TokenType.ERC20),
  title: "",
  contractId: 0,
  rentStatus: RentRuleStatus.NEW,
};

export const Rent = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IRent>
  >(RENT_URL, { enableInfiniteScroll: isMd }, void 0, {
    rentStatus: [RentRuleStatus.ACTIVE, RentRuleStatus.NEW],
  });

  const { handleForm, mode, selectedItem, formDialogUniqueKey } = useDialogForm<IRent>();

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "rent"]} />

      <PageHeader message="pages.rent.title">
        <Button
          intent="secondary"
          startIcon={<Add />}
          onClick={handleForm(RENT_DIALOG_KEY, emptyValues, "create")}
          data-testid="ExchangeCreateButton"
        >
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <SearchWithFilters initialValues={{ rentStatus: [RentRuleStatus.ACTIVE, RentRuleStatus.NEW] }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 6 }}>
            <EntityInput
              name="contractIds"
              controller="contracts"
              multiple
              fetchData={{ contractFeatures: [ContractFeatures.RENTABLE] }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SelectInput multiple name="rentStatus" options={RentRuleStatus} />
          </Grid>
        </Grid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(rent => (
            <StyledListItem key={rent.id}>
              <List.ItemText sx={{ width: 0.5 }}>{rent.title}</List.ItemText>
              <List.ItemText sx={{ width: 0.5 }}>{rent.contract?.title}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={handleForm(RENT_DIALOG_KEY, rent, "edit")}
                  message="form.buttons.edit"
                  dataTestId="RentEditButton"
                  icon={Create}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <RentDialogForm
        open={formDialogUniqueKey === `${RENT_DIALOG_KEY}.${mode}`}
        mode={mode}
        selectedItem={selectedItem}
        url={RENT_URL}
        refetch={refetch}
      />
    </Grid>
  );
};
